/**
 * Integration widgets composable.
 *
 * Fetches the widget catalogue / a single widget schema for the template
 * builder, and resolves the `{integration.*}` / `{widget.*}` placeholders of
 * an `image_url_template`. `{var=…}` Shopimind variables are left literal
 * (resolved per recipient at send time).
 *
 * Backend: WidgetsController (`GET /v1/widgets/catalog`, `GET /v1/widgets/:slug/:key`).
 */

import { ref } from 'vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { substituteIntegrationWidgetUrl } from '@/components/template-builder/utils/integration-widget-url';
import type {
  WidgetCatalogEntry,
  WidgetSchemaResponse,
  WidgetTarget,
  WidgetRenderResult,
} from '@/types/integrations';

/**
 * Erreur dédiée au rendu d'un widget d'intégration. Portée par `renderIntegrationWidgetApi`
 * pour que les appelants (setters template-builder) distinguent un échec réseau/serveur
 * d'un rendu vide légitime, et affichent un bandeau/toast au lieu d'avaler l'erreur (G3).
 */
export class IntegrationWidgetRenderError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'IntegrationWidgetRenderError';
  }
}

/**
 * Cache module-level du catalogue de widgets, par cible d'éditeur (G2). Un remount
 * du panneau de widgets ne redéclenche plus un fetch réseau tant que l'entrée est
 * fraîche (TTL 5 min). `fetchCatalog({ force: true })` contourne le cache
 * (utilisé notamment par le retry au drop quand le catalogue est encore vide).
 */
const CATALOG_TTL_MS = 5 * 60 * 1000;
const catalogCache = new Map<WidgetTarget, { data: WidgetCatalogEntry[]; ts: number }>();

/** Vide le cache du catalogue (tests / invalidation manuelle). */
export function clearIntegrationWidgetsCatalogCache(): void {
  catalogCache.clear();
}

/**
 * Render an html widget server-side: POST /v1/widgets/render → sanitized HTML.
 * Standalone (not part of the composable) so the template-builder setters can
 * call it outside a Vue setup context. THROWS `IntegrationWidgetRenderError` on
 * failure (network/server) instead of silently returning '' — the caller decides
 * whether to show a banner (first drop) or a toast (re-render) and keep the
 * previous snapshot (G3).
 */
export async function renderIntegrationWidgetApi(
  slug: string,
  key: string,
  config: Record<string, unknown>,
  target: WidgetTarget = 'email_template',
): Promise<WidgetRenderResult> {
  try {
    const response = await nestCall(
      'post',
      NestApiType.CLIENT_API,
      '/widgets/render',
      {},
      '',
      { slug, key, config, target },
      true,
    );
    const data = (response?.data as WidgetRenderResult) ?? { html: '' };
    return {
      html: data.html ?? '',
      css: data.css,
      javascript: data.javascript,
    };
  } catch (error) {
    console.error(`Error rendering widget ${slug}/${key}:`, error);
    throw new IntegrationWidgetRenderError(
      `Failed to render widget ${slug}/${key}`,
      error,
    );
  }
}

export function useIntegrationWidgets() {
  const catalog = ref<WidgetCatalogEntry[]>([]);
  const loadingCatalog = ref(false);

  /**
   * Widget catalogue for a given editor target (grouped by integration).
   * Cached module-level (TTL 5 min) so panel remounts reuse the last result;
   * pass `{ force: true }` to bypass the cache (drop-time retry).
   */
  async function fetchCatalog(
    target: WidgetTarget = 'email_template',
    options: { force?: boolean } = {},
  ): Promise<WidgetCatalogEntry[]> {
    const cached = catalogCache.get(target);
    if (!options.force && cached && Date.now() - cached.ts < CATALOG_TTL_MS) {
      catalog.value = cached.data;
      return cached.data;
    }
    loadingCatalog.value = true;
    try {
      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/widgets/catalog',
        {},
        '',
        undefined,
        true,
        { target },
      );
      const data = (response?.data as WidgetCatalogEntry[]) ?? [];
      catalog.value = data;
      catalogCache.set(target, { data, ts: Date.now() });
      return data;
    } catch (error) {
      console.error('Error loading widgets catalog:', error);
      // En échec on renvoie le dernier cache connu s'il existe (mieux que vide),
      // sinon une liste vide — sans écraser le cache d'une valeur fautive.
      if (cached) {
        catalog.value = cached.data;
        return cached.data;
      }
      catalog.value = [];
      return [];
    } finally {
      loadingCatalog.value = false;
    }
  }

  /** Full schema + non-sensitive integration config values for one widget. */
  async function fetchWidgetSchema(
    slug: string,
    key: string,
  ): Promise<WidgetSchemaResponse | null> {
    try {
      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        `/widgets/${slug}/${key}`,
        {},
        '',
        undefined,
        true,
      );
      return (response?.data as WidgetSchemaResponse) ?? null;
    } catch (error) {
      console.error(`Error loading widget schema ${slug}/${key}:`, error);
      return null;
    }
  }

  /**
   * Resolve `{integration.<key>}` placeholders from the shop's (non-sensitive)
   * integration config. Done once at drop and baked into `data-url-template`.
   */
  function resolveIntegrationPlaceholders(
    template: string,
    integrationConfigValues: Record<string, string>,
  ): string {
    return (template || '').replace(
      /\{integration\.([a-zA-Z0-9_]+)\}/g,
      (_match, key) => encodeURIComponent(integrationConfigValues?.[key] ?? ''),
    );
  }

  /**
   * Build the full image URL: resolve `{integration.*}` then `{widget.*}`,
   * leaving `{var=*}` literal. Convenience for an at-drop preview — the placed
   * block stores the `{integration.*}`-resolved template + config and
   * re-renders through the setters.
   */
  function buildImageUrl(
    imageUrlTemplate: string,
    widgetConfig: Record<string, unknown>,
    integrationConfigValues: Record<string, string>,
  ): string {
    const withIntegration = resolveIntegrationPlaceholders(
      imageUrlTemplate,
      integrationConfigValues,
    );
    return substituteIntegrationWidgetUrl(withIntegration, widgetConfig);
  }

  return {
    catalog,
    loadingCatalog,
    fetchCatalog,
    fetchWidgetSchema,
    resolveIntegrationPlaceholders,
    buildImageUrl,
  };
}
